/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useContext } from 'react';
import {
  refreshSearchParams,
  SearchAggregatorDeeplinkResponse,
} from '../types/FlightSearch.types';

export type openModuleProps = {
  widget:
    | 'flights'
    | 'flight_deeplink'
    | 'hotels'
    | 'hotel_deeplink'
    | 'snap_deeplink'
    | 'customerJourney'
    | 'movetix'
    | string;
  deeplink: string;
  // Below fields are only used with widget: 'flight_deeplink', to hand off
  // to OTA's native PostBookingAddOns screen — the host's openModule
  // forwards them through as route params for that screen (see
  // FlightsBookingConfirmation.tsx's handleAddonsNativeRedirect).
  redirectionUrl?: string;
  deepLinkId?: string;
  airlineProfile?: string;
  isPostBooking?: boolean;
  bookingNumber?: string;
  sourceScreen?: string;
};

export type backHandlerProps = {
  goBackToSuperApp: boolean;
};

export type onFlightSelectParams = {
  promoCode?: string;
  refreshSearch?: (params: refreshSearchParams) => void;
};

export interface FlightProps {
  backHandler: (params?: backHandlerProps) => void;
  // Whether calling backHandler() would land on a real previous screen in
  // the host app's own navigation stack, as opposed to just resetting to
  // Homepage. Screens that are their own standalone nested router (this
  // whole <Flights> instance) have no visibility into that stack otherwise
  // - router.canGoBack() only ever reflects navigation within this module.
  canGoBackToHost?: () => boolean;
  currency: string;
  deeplink: string;
  locale: string;
  openModule: (data: openModuleProps) => void;
  onSearchPress?: () => void;
  onFlightSelect?: (
    data?: SearchAggregatorDeeplinkResponse,
    params?: onFlightSelectParams,
  ) => void;
  onPressEdit?: () => void;
  // Explicit initial-route override so the host app can mount straight into a
  // nested screen (e.g. booking-confirmation) without first flashing the
  // search widget while waiting for a deeplink URL to be parsed. This is a
  // one-shot signal - consumers must call clearInitialScreenPath() once
  // they've acted on it, otherwise it stays "sticky" for the lifetime of
  // <Flights> and re-triggers on every subsequent visit to index (e.g. after
  // pressing back), causing a redirect loop.
  initialScreenPath?: string;
  initialScreenParams?: Record<string, any>;
  clearInitialScreenPath?: () => void;
}

const SuperAppContext = React.createContext<FlightProps>({
  openModule: ({ widget, deeplink }) => {
    console.log(
      'superAppNavigation called: widget',
      widget,
      ' deeplink: ',
      deeplink,
    );
  },
  backHandler: (params) => {
    console.log('Backhandler called with params: ', params);
  },
  currency: 'MYR',
  deeplink: '',
  locale: 'en-gb',
});

const useSuperAppContext = () => {
  const value = useContext(SuperAppContext);
  return value;
};

export { SuperAppContext, useSuperAppContext };
